import { useState, useCallback } from "react";
import { StepIndicator, StepNavigation } from "./StepIndicator";
import { Step1Upload } from "./Step1Upload";
import { Step2Config } from "./Step2Config";
import type { SelectedDriveFile } from "./GoogleDrivePicker";
import { instructionsExceedLimit } from "@/components/form/InstructionWordLimit";
import type { SupportingDocInput } from "@/types";

export interface WizardData {
  selectedFile: File | null;
  selectedDriveFile: SelectedDriveFile | null;
  contractType: string;
  backgroundInfo: string;
  supportingDocs: SupportingDocInput[];
  jurisdiction: string;
  userRole: string;
  agentInstructions: string;
  skipResearch: boolean;
}

interface ReviewWizardProps {
  initialData?: Partial<WizardData>;
  onSubmit: (data: WizardData) => void;
  isSubmitting?: boolean;
}

const defaultData: WizardData = {
  selectedFile: null,
  selectedDriveFile: null,
  contractType: "MSA",
  backgroundInfo: "",
  supportingDocs: [],
  jurisdiction: "Luxembourg",
  userRole: "senior-counsel",
  agentInstructions: "",
  skipResearch: true,
};

export function ReviewWizard({
  initialData,
  onSubmit,
  isSubmitting = false,
}: ReviewWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<WizardData>({
    ...defaultData,
    ...initialData,
  });

  const updateField = useCallback(
    <K extends keyof WizardData>(field: K, value: WizardData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleNext = useCallback(() => {
    if (currentStep < 2) {
      setCurrentStep((prev) => prev + 1);
    } else {
      onSubmit(formData);
    }
  }, [currentStep, formData, onSubmit]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const handleStepClick = useCallback(
    (step: number) => {
      // Only allow going to completed steps or current step
      if (step <= currentStep || step === 1) {
        setCurrentStep(step);
      }
    },
    [currentStep]
  );

  const isStep1Valid = formData.selectedFile !== null || formData.selectedDriveFile !== null;
  const isStep2Valid =
    formData.jurisdiction.trim() !== "" &&
    formData.userRole.trim() !== "" &&
    !instructionsExceedLimit(formData.agentInstructions);

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Step Indicator */}
      <div className="mb-8">
        <StepIndicator
          currentStep={currentStep}
          onStepClick={handleStepClick}
        />
      </div>

      {/* Step Content */}
      <div className="rounded-2xl border border-[var(--lm-border-card)] bg-white p-6 shadow-[var(--shadow-card)] dark:border-slate-800 dark:bg-slate-900/50 dark:shadow-xl">
        {currentStep === 1 && (
          <Step1Upload
            selectedFile={formData.selectedFile}
            onFileSelect={(file) => updateField("selectedFile", file)}
            selectedDriveFile={formData.selectedDriveFile}
            onDriveFileSelect={(file) => updateField("selectedDriveFile", file)}
            contractType={formData.contractType}
            onContractTypeChange={(value) => updateField("contractType", value)}
            backgroundInfo={formData.backgroundInfo}
            onBackgroundInfoChange={(value) => updateField("backgroundInfo", value)}
            supportingDocs={formData.supportingDocs}
            onSupportingDocsChange={(docs) => updateField("supportingDocs", docs)}
          />
        )}

        {currentStep === 2 && (
          <Step2Config
            jurisdiction={formData.jurisdiction}
            onJurisdictionChange={(value) => updateField("jurisdiction", value)}
            userRole={formData.userRole}
            onUserRoleChange={(value) => updateField("userRole", value)}
            agentInstructions={formData.agentInstructions}
            onAgentInstructionsChange={(value) =>
              updateField("agentInstructions", value)
            }
            skipResearch={formData.skipResearch}
            onSkipResearchChange={(value) => updateField("skipResearch", value)}
          />
        )}

        {/* Navigation */}
        <StepNavigation
          currentStep={currentStep}
          totalSteps={2}
          onBack={handleBack}
          onNext={handleNext}
          isNextDisabled={
            isSubmitting ||
            (currentStep === 1 && !isStep1Valid) ||
            (currentStep === 2 && !isStep2Valid)
          }
          nextLabel={currentStep === 2 ? "Analyze Contract" : "Continue"}
        />
      </div>
    </div>
  );
}
